import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { PrimaryButton } from '@components/Button/variants';
import TextAreaField from '@components/Form/TextAreaField';
import TokenizeTextField from '@components/Form/TokenizeTextField';
import useAiCohortNotificationsUserCreate from '@hooks/useAiCohortNotificationsUserCreate';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ContentType, click } from '@tracking';
import { AutocompleteOption } from '@utils/interfaces';

const MAX_USER_NOTE_CHAR_COUNT = 200;

interface ShareFormValues {
  emailAddresses: AutocompleteOption[];
  userNote: string;
}

const INITIAL_VALUES: ShareFormValues = {
  emailAddresses: [],
  userNote: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validationSchema = Yup.object({
  emailAddresses: Yup.array()
    .min(1, 'At least one email address is required')
    .required('Email addresses are required')
    .test('valid-emails', 'Please enter valid email addresses', (emails) => {
      if (!emails || emails.length === 0) return false;
      return emails.every((email) => EMAIL_REGEX.test(email.value));
    }),
  userNote: Yup.string().max(500, 'Note must be 500 characters or less'),
});

interface ShareFormProps {
  aiCohortNotificationId: number;
  onSuccess: () => void;
}

const ShareForm: React.FC<ShareFormProps> = ({ aiCohortNotificationId, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { mutateAsync } = useAiCohortNotificationsUserCreate();
  const { trackEvent } = useEventTracking();

  const handleSubmit = async (values: ShareFormValues, { setSubmitting, resetForm }) => {
    setError(null);
    setSuccessMessage(null);

    try {
      const emailAddresses = values.emailAddresses.map((email) => email.value.trim());
      const userNote = values.userNote.trim();

      const response = await mutateAsync({
        aiCohortNotificationId,
        emailAddresses,
        userNote,
      });

      if (response.success) {
        setSuccessMessage('Personal Briefing shared successfully!');
        resetForm();
        onSuccess();
        trackEvent(
          click({
            clickTarget: ClickTarget.PersonalBriefingShare,
            contentType: ContentType.PersonalBriefing,
            contentId: aiCohortNotificationId,
            clickText: `Sent to ${emailAddresses.length} email addresses`,
          })
        );
      } else {
        setError(response.error || 'Failed to share Personal Briefing. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-32">
      {successMessage && (
        <div className="rounded mb-24 border border-solid border-green-500 bg-green-50 p-16 font-suisse-intl text-body-md text-green-700">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="rounded mb-24 border border-solid border-red-500 bg-red-50 p-16 font-suisse-intl text-body-md text-red-700">
          {error}
        </div>
      )}

      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form noValidate>
            <TokenizeTextField
              className="mb-24"
              name="emailAddresses"
              label="Email Addresses"
              placeholder="Enter email addresses separated by commas"
              styles={{
                control: (base) => ({ ...base, borderRadius: '25px' }),
              }}
            />

            <TextAreaField
              name="userNote"
              label="Note to Recipient (Optional)"
              placeholder="Add a personal note…"
              maxCharCount={MAX_USER_NOTE_CHAR_COUNT}
              rows={4}
              className="mb-24"
            />

            <PrimaryButton
              as="button"
              type="submit"
              className="border-pro-gold bg-pro-gold text-white enabled:hover:border-pro-lightgold enabled:hover:bg-pro-lightgold enabled:hover:text-pro-gold"
              disabled={!formik.isValid || formik.isSubmitting}
              isLoading={formik.isSubmitting}
              fullWidth={false}
            >
              Share briefing
            </PrimaryButton>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ShareForm;
